import parsePhoneNumber, { AsYouType } from 'libphonenumber-js';

/**
 * Validates if a phone number is a valid US phone number
 * Accepts various formats like:
 * - (555) 123-4567
 * - 555-123-4567
 * - 555.123.4567
 * - 555 123 4567
 * - +1 (555) 123-4567
 * - +15551234567
 * - 5551234567
 */
export function isValidUSPhoneNumber(phoneNumber: string): boolean {
  if (!phoneNumber) return false;
  
  try {
    // Parse the phone number, defaulting to US if no country code provided
    const parsed = parsePhoneNumber(phoneNumber, 'US');
    
    // Check if it's valid and is a US number
    return parsed?.isValid() === true && parsed?.country === 'US';
  } catch {
    return false;
  }
}

/**
 * Normalizes a US phone number to E.164 format (+1XXXXXXXXXX)
 * Returns the normalized number or null if invalid
 */
export function normalizeUSPhoneNumber(phoneNumber: string): string | null {
  if (!phoneNumber) return null;
  
  try {
    const parsed = parsePhoneNumber(phoneNumber, 'US');
    
    if (parsed?.isValid() === true && parsed?.country === 'US') {
      return parsed.number;
    }
    
    return null;
  } catch {
    return null;
  }
}

/**
 * Formats a phone number as the user types for better UX
 * Returns formatted string in US national format: (555) 123-4567
 */
export function formatPhoneNumberAsYouType(phoneNumber: string): string {
  if (!phoneNumber) return '';
  
  try {
    const formatter = new AsYouType('US');
    return formatter.input(phoneNumber);
  } catch {
    return phoneNumber;
  }
}

/**
 * Formats a complete US phone number to national format
 * Returns formatted string like: (555) 123-4567
 */
export function formatUSPhoneNumber(phoneNumber: string): string {
  if (!phoneNumber) return '';
  
  try {
    const parsed = parsePhoneNumber(phoneNumber, 'US');
    
    if (parsed?.isValid() === true && parsed?.country === 'US') {
      return parsed.formatNational();
    }
    
    return phoneNumber;
  } catch {
    return phoneNumber;
  }
} 